<template>
  <div class="deal-documents">
    <div v-if="isLoadingDocumentTypes" class="documents-loading mb-4">
      <v-progress-linear indeterminate color="primary" />
    </div>

    <div v-else class="deal-document-slots mb-4">
      <div
        v-for="documentType in documentTypes"
        :key="documentType.id"
        class="preview-field deal-document-slot"
        :class="{
          'deal-document-slot--required-missing': isRequiredMissing(documentType),
        }"
      >
        <div
          class="deal-document-slot__header"
          :class="{
            'deal-document-slot__header--compact':
              !getDocumentsForType(documentType.id).length &&
              uploadingDocumentTypeId !== documentType.id,
          }"
        >
          <div class="preview-label deal-document-slot__label">
            <span>{{ documentType.label }}</span>
            <v-chip
              v-if="documentType.required"
              x-small
              label
              color="error"
              text-color="white"
              class="ml-2"
            >
              {{ $t("DEALS.MODAL.REQUIRED_DOCUMENT") }}
            </v-chip>
          </div>

          <div
            v-if="hasPermissions('deals-edit')"
            class="deal-files-dropzone deal-document-slot__dropzone"
            :class="{
              'deal-files-dropzone--active': activeDropzoneTypeId === documentType.id,
              'deal-document-dropzone--required': documentType.required,
            }"
            @click="openDocumentPicker(documentType.id)"
            @dragenter.prevent="activeDropzoneTypeId = documentType.id"
            @dragover.prevent="activeDropzoneTypeId = documentType.id"
            @dragleave.prevent="activeDropzoneTypeId = null"
            @drop.prevent="onDropDocument($event, documentType.id)"
          >
            <v-progress-circular
              v-if="uploadingDocumentTypeId === documentType.id"
              indeterminate
              size="20"
              width="2"
              color="primary"
              class="mr-2"
            />
            <v-icon v-else color="primary" class="mr-2">mdi-tray-arrow-up</v-icon>
            <span>{{ $t("DEALS.MODAL.UPLOAD_DOCUMENT_HINT") }}</span>
          </div>
        </div>

        <div
          class="preview-value"
          :class="{
            'preview-value--empty':
              !getDocumentsForType(documentType.id).length &&
              uploadingDocumentTypeId !== documentType.id,
          }"
        >
          <v-progress-linear
            v-if="uploadingDocumentTypeId === documentType.id"
            indeterminate
            color="primary"
            class="mb-3"
          />

          <template v-if="getDocumentsForType(documentType.id).length">
            <div
              v-for="document in getDocumentsForType(documentType.id)"
              :key="document.id"
              class="deal-file-row"
            >
              <div class="deal-file-name">
                <v-icon small class="mr-2">mdi-file-outline</v-icon>
                <span>{{ document.file?.original_name || document.file?.name }}</span>
              </div>
              <div class="deal-file-actions">
                <span class="deal-file-size mr-3">
                  {{ formatFileSize(document.file?.size || 0) }}
                </span>
                <v-btn
                  icon
                  small
                  color="primary"
                  :loading="previewingFileId === document.file?.id"
                  :disabled="previewingFileId !== null"
                  :aria-label="$t('GENERAL.LABELS.PREVIEW')"
                  @click="previewDocumentFile(document)"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="primary"
                  :aria-label="$t('GENERAL.LABELS.DOWNLOAD')"
                  @click="downloadDocumentFile(document)"
                >
                  <v-icon small>mdi-download</v-icon>
                </v-btn>
                <v-btn
                  v-if="hasPermissions('deals-edit')"
                  icon
                  small
                  color="error"
                  :loading="deletingDocumentFileId === document.file?.id"
                  :disabled="deletingDocumentFileId !== null || uploadingDocumentTypeId !== null"
                  :aria-label="$t('GENERAL.LABELS.DELETE')"
                  @click="deleteDocumentFile(document)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </template>
        </div>
        <div
          v-if="isRequiredMissing(documentType)"
          class="deal-document-slot__hint"
        >
          {{ $t("DEALS.MODAL.MISSING_REQUIRED_DOCUMENT") }}
        </div>
      </div>
    </div>

    <input
      ref="documentFileInput"
      type="file"
      class="d-none"
      multiple
      @change="onDocumentFileInputChange"
    >
  </div>
</template>

<script>
import { SHOW_SNACK } from "@/store/snack.module";
import {
  UPLOAD_DEAL_DOCUMENT,
  DELETE_DEAL_DOCUMENT_FILE,
  DOWNLOAD_DEAL_DOCUMENT_FILE,
  PREVIEW_DEAL_DOCUMENT_FILE,
} from "@/store/deals.module";
import { GET_DEAL_DOCUMENT_TYPE_OPTIONS } from "@/store/dealDocumentTypes.module";
import formatter from "@/common/formatter.service";

export default {
  name: "DealModalDocuments",
  props: {
    dealId: {
      type: [Number, String],
      default: null,
    },
    initialDocuments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      documentTypes: [],
      documents: [],
      isLoadingDocumentTypes: false,
      uploadingDocumentTypeId: null,
      deletingDocumentFileId: null,
      previewingFileId: null,
      activeDropzoneTypeId: null,
      pendingDocumentTypeId: null,
    };
  },
  watch: {
    initialDocuments: {
      immediate: true,
      handler(documents) {
        this.documents = Array.isArray(documents) ? [...documents] : [];
      },
    },
    dealId: {
      immediate: true,
      handler() {
        this.loadDocumentTypes();
      },
    },
  },
  methods: {
    loadDocumentTypes() {
      this.isLoadingDocumentTypes = true;

      this.$store
        .dispatch(GET_DEAL_DOCUMENT_TYPE_OPTIONS)
        .then((response) => {
          this.documentTypes = Array.isArray(response?.data) ? response.data : [];
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isLoadingDocumentTypes = false;
        });
    },
    getDocumentsForType(documentTypeId) {
      return this.documents.filter(
        (document) => Number(document.deal_document_type?.id) === Number(documentTypeId)
      );
    },
    isRequiredMissing(documentType) {
      return documentType.required && !this.getDocumentsForType(documentType.id).length;
    },
    emitDocumentsUpdated() {
      this.$emit("documents-updated", [...this.documents]);
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    openDocumentPicker(documentTypeId) {
      if (!this.dealId || this.uploadingDocumentTypeId !== null) {
        return;
      }

      this.pendingDocumentTypeId = documentTypeId;
      this.$refs.documentFileInput?.click();
    },
    onDocumentFileInputChange(event) {
      const selectedFiles = Array.from(event?.target?.files || []);
      const documentTypeId = this.pendingDocumentTypeId;

      if (!selectedFiles.length || !documentTypeId) {
        return;
      }

      this.uploadDocumentFiles(documentTypeId, selectedFiles);

      this.pendingDocumentTypeId = null;
      if (this.$refs.documentFileInput) {
        this.$refs.documentFileInput.value = "";
      }
    },
    onDropDocument(event, documentTypeId) {
      this.activeDropzoneTypeId = null;

      const selectedFiles = Array.from(event?.dataTransfer?.files || []);
      if (!selectedFiles.length || !this.dealId) {
        return;
      }

      this.uploadDocumentFiles(documentTypeId, selectedFiles);
    },
    uploadDocumentFiles(documentTypeId, files) {
      if (!this.dealId || !files.length) {
        return;
      }

      const formData = new FormData();
      formData.append("deal_document_type_id", documentTypeId);
      files.forEach((file) => {
        formData.append("files[]", file);
      });

      this.uploadingDocumentTypeId = documentTypeId;

      this.$store
        .dispatch(UPLOAD_DEAL_DOCUMENT, {
          dealId: this.dealId,
          params: formData,
        })
        .then((uploadedDocuments) => {
          this.documents = [...this.documents, ...uploadedDocuments];
          this.emitDocumentsUpdated();
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.uploadingDocumentTypeId = null;
        });
    },
    deleteDocumentFile(document) {
      const fileId = document?.file?.id;

      if (!this.dealId || !document?.id || !fileId || this.deletingDocumentFileId !== null) {
        return;
      }

      this.deletingDocumentFileId = fileId;

      this.$store
        .dispatch(DELETE_DEAL_DOCUMENT_FILE, {
          dealId: this.dealId,
          documentId: document.id,
          fileId,
        })
        .then(() => {
          this.documents = this.documents.filter(
            (item) => Number(item.id) !== Number(document.id)
          );
          this.emitDocumentsUpdated();
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.deletingDocumentFileId = null;
        });
    },
    downloadDocumentFile(document) {
      const file = document?.file;

      if (!this.dealId || !document?.id || !file?.id) {
        return;
      }

      this.$store
        .dispatch(DOWNLOAD_DEAL_DOCUMENT_FILE, {
          dealId: this.dealId,
          documentId: document.id,
          fileId: file.id,
          originalName: file.original_name,
          name: file.name,
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        });
    },
    previewDocumentFile(document) {
      const file = document?.file;

      if (!this.dealId || !document?.id || !file?.id || this.previewingFileId !== null) {
        return;
      }

      this.previewingFileId = file.id;

      this.$store
        .dispatch(PREVIEW_DEAL_DOCUMENT_FILE, {
          dealId: this.dealId,
          documentId: document.id,
          fileId: file.id,
        })
        .then((url) => {
          if (url) {
            window.open(url, "_blank");
          }
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.previewingFileId = null;
        });
    },
    formatFileSize: formatter.formatFileSize,
  },
};
</script>

<style scoped>
.deal-documents {
  display: flex;
  flex-direction: column;
}

.deal-document-slots {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.preview-field {
  background: #f8f9fc;
  border: 1px solid #e3e6ef;
  border-radius: 8px;
  padding: 12px 14px;
  min-height: 74px;
}

.deal-document-slot--required-missing {
  border-color: #f44336;
  background: #fff8f8;
}

.deal-document-slot__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.deal-document-slot__header--compact {
  margin-bottom: 0;
}

.preview-label {
  font-size: 12px;
  text-transform: uppercase;
  color: #7f8899;
  margin-bottom: 6px;
}

.deal-document-slot__label {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 0;
}

.deal-document-slot__dropzone {
  flex: 1 1 340px;
  min-width: 280px;
  max-width: 520px;
  margin-bottom: 0 !important;
}

.preview-value {
  color: #212529;
  font-size: 15px;
  line-height: 1.4;
  word-break: break-word;
}

.preview-value--empty {
  display: none;
}

.deal-document-slot__hint {
  margin-top: 6px;
  color: #d32f2f;
  font-size: 13px;
}

.deal-document-dropzone--required {
  border-color: #ef9a9a;
}

.deal-files-dropzone {
  border: 2px dashed #b8c2d6;
  border-radius: 8px;
  padding: 14px;
  color: #4b5672;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.deal-files-dropzone--active {
  border-color: #1976d2;
  background-color: #eef5ff;
}

.deal-file-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e3e6ef;
  border-radius: 6px;
  padding: 8px 10px;
}

.deal-file-row + .deal-file-row {
  margin-top: 8px;
}

.deal-file-name {
  display: flex;
  align-items: center;
  min-width: 0;
}

.deal-file-name span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.deal-file-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.deal-file-size {
  color: #7f8899;
  font-size: 13px;
}

@media (max-width: 600px) {
  .deal-document-slot__dropzone {
    min-width: 0;
    max-width: none;
    width: 100%;
  }
}
</style>
